import React, { useState, useRef, ChangeEvent, FocusEvent } from 'react';
import moment from 'moment';

type DateInputFieldProps = {
    row: Record<string, any>;
    name: string;
    disabled?: boolean;
    setIsSavedResult: (saved: boolean) => void;
    certificate: any;
    setCertificate: (certificate: any) => void;
    editable: boolean;
    showAccount: any;
    setUpdatedAccountIndexList:any;
    updatedAccountIndexList: any;
};

const DateInputField: React.FC<DateInputFieldProps> = ({
                                                           row = {},
                                                           name = '',
                                                           disabled,
                                                           setIsSavedResult,
                                                           certificate,
                                                           setCertificate,
                                                           editable,
                                                           showAccount,
                                                           setUpdatedAccountIndexList,
                                                           updatedAccountIndexList,
                                                       }) => {
    const [inputValue, setInputValue] = useState<string>(() => {
        if (row && name && row[name]) {
            const localDateTime = moment.utc(row[name]).local();
            return localDateTime.format('MM-DD-YYYY');
        }
        return 'MM/DD/YYYY';
    });

    const [error, setError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    const validateDate = (mm: number, dd: number, yyyy: number): string => {
        if (mm < 1 || mm > 12) return 'Month must be between 01 and 12.';
        if (dd < 1 || dd > 31) return 'Day must be between 01 and 31.';
        if ([4, 6, 9, 11].includes(mm) && dd > 30) return 'Selected month has only 30 days.';
        if (mm === 2) {
            const isLeapYear = (yyyy % 4 === 0 && yyyy % 100 !== 0) || (yyyy % 400 === 0);
            if (dd > (isLeapYear ? 29 : 28)) {
                return isLeapYear ? 'February has only 29 days in a leap year.' : 'February has only 28 days.';
            }
        }
        if (yyyy < 1950) return 'Year must be 1950 or later.';
        return '';
    };

    const updateCertificate = (updatedRow: any) => {
        const updatedAccounts = certificate.accounts.map((acc: any, index: number) => {
            if (showAccount == index) {
                return { ...acc, [name]: updatedRow[name], taxDataSource: 'EDITOR' };
            }
            return acc;
        });
        setUpdatedAccountIndexList((prev:any) => {
            if (!prev.includes(showAccount)) {
                return [...prev, showAccount];
            }
            return prev;
        });
        setCertificate({ ...certificate, accounts: updatedAccounts });
    };

    const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const cursorPosition = event.target.selectionStart || 0;
        const previousValue = inputValue;

        let maskedValue = value.replace(/\D/g, '');
        if (maskedValue.length > 2) maskedValue = maskedValue.slice(0, 2) + '-' + maskedValue.slice(2);
        if (maskedValue.length > 5) maskedValue = maskedValue.slice(0, 5) + '-' + maskedValue.slice(5);
        if (maskedValue.length > 10) maskedValue = maskedValue.slice(0, 10);

        const isDeleting = previousValue.length > maskedValue.length;
        let newCursorPosition = cursorPosition;

        if (isDeleting && previousValue[cursorPosition - 1] === '-') {
            newCursorPosition -= 1;
        } else if (!isDeleting && maskedValue[cursorPosition - 1] === '-') {
            newCursorPosition += 1;
        }

        setInputValue(maskedValue);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
            }
        }, 0);

        if (maskedValue.length === 10) {
            const [mm, dd, yyyy] = maskedValue.split('-').map(Number);
            const validationError = validateDate(mm, dd, yyyy);

            if (!validationError && moment(maskedValue, 'MM-DD-YYYY', true).isValid()) {
                row[name] = moment(maskedValue, 'MM-DD-YYYY');
                setIsSavedResult(false);

                updateCertificate(row);
                setError('');
            } else {
                setError(validationError || 'Invalid date format. Please use MM-DD-YYYY.');
            }
        } else if (maskedValue === '') {
            row[name] = null;
            setIsSavedResult(false);

            updateCertificate(row);
            setError('');
        } else {
            setError('');
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (inputValue.length === 10) {
            const [mm, dd, yyyy] = inputValue.split('-').map(Number);
            const validationError = validateDate(mm, dd, yyyy);
            if (!validationError && moment(inputValue, 'MM-DD-YYYY', true).isValid()) {
                row[name] = moment(inputValue, 'MM-DD-YYYY');
                setIsSavedResult(false);

                updateCertificate(row);
                setError('');
            } else {
                setError(validationError || 'Invalid date format. Please use MM-DD-YYYY.');
            }
        } else if (inputValue === '') {
            row[name] = null;
            setIsSavedResult(false);

            updateCertificate(row);
            setError('');
        } else {
            setError('Date should be in MM-DD-YYYY format.');
        }
    };

    const clearDate = () => {
        row[name] = null;
        setIsSavedResult(false);

        updateCertificate(row);
        setInputValue('');
        setError('');
    };

    const handleDatePickerChange = (event: ChangeEvent<HTMLInputElement>) => {
        const isoDate = event.target.value;
        const formattedDate = moment.utc(isoDate).format('MM-DD-YYYY');
        setInputValue(formattedDate);
        row[name] = isoDate ? moment(isoDate.toUpperCase()) : null;
        setIsSavedResult(false);

        updateCertificate(row);
        setError('');
    };

    return (
        <>
            <div className={disabled ? 'date-input-container custom_datepicker disabled_date relative p-3' : 'date-input-container custom_datepicker relative p-[10px] bg-text-0'}>
                <div className="calendar_select absolute right-2">
                    <i className={'fa fa-calendar '}></i>
                    <input
                        type="date"
                        value={row[name] ? moment.utc(row[name]).local().format('YYYY-MM-DD') : ''}
                        onChange={handleDatePickerChange}
                        disabled={disabled || !editable}
                        onKeyDown={(e) => e.preventDefault()}
                        className={'absolute opacity-0 right-0'}
                    />
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    className="editable_input"
                    maxLength={10}
                    disabled={disabled || !editable}
                    placeholder="MM-DD-YYYY"
                />
                {inputValue && !disabled && editable && (
                    <button className="clear-date-button absolute right-[30px]" onClick={clearDate}>
                        &#10006;
                    </button>
                )}
            </div>
            {error && <span className="error-message" style={{ fontSize: '12px', color: 'red' }}>{error}</span>}
        </>
    );
};

export default DateInputField;
